/**
 * Accessibility Panel - Alpine.js Component
 * Manages accessibility settings, profiles, and persistence
 */

document.addEventListener("alpine:init", () => {
    window.Alpine.data("accessibilityPanel", () => ({
        showPanel: false,
        profiles: {},
        settingsDefinition: {},
        rangeSettings: {},
        selectSettings: {},
        booleanSettings: {},
        settings: {},
        loading: false,
        selectedProfile: null,
        nonce: null,
        restUrl: null,
        displayMode: 'both', // 'detailed', 'buttons', or 'both'
        currentView: 'detailed', // Current active view when 'both' mode

        /**
         * Separate settings by type for easier rendering
         */
        separateSettingsByType() {
            this.rangeSettings = {};
            this.selectSettings = {};
            this.booleanSettings = {};

            Object.entries(this.settingsDefinition).forEach(([id, setting]) => {
                if (setting.type === "range") {
                    this.rangeSettings[id] = setting;
                } else if (setting.type === "select") {
                    this.selectSettings[id] = setting;
                } else if (setting.type === "boolean") {
                    this.booleanSettings[id] = setting;
                }
            });
        },

        /**
         * Initialize settings with defaults from settings definition
         */
        initializeSettingsWithDefaults() {
            Object.entries(this.settingsDefinition).forEach(([id, setting]) => {
                // Set default value if not already set
                if (!(id in this.settings)) {
                    this.settings[id] = setting.default;
                }
            });
        },

        /**
         * Initialize component
         */
        async init() {
            await this.loadSettings();
            this.initializeSettingsWithDefaults();
            this.separateSettingsByType();

            // Check if we have stored settings (preload would have applied them)
            const hasStoredSettings = localStorage.getItem("a11y_settings");

            this.restoreFromLocalStorage();

            // Only apply settings if preload didn't (first visit without localStorage)
            if (!hasStoredSettings) {
                this.applySettings();
            }

            // Listen for settings changes from other tabs/windows
            window.addEventListener("storage", (e) => {
                if (e.key === "a11y_settings") {
                    // Restore from storage and apply the changes
                    this.restoreFromLocalStorage();
                    this.applySettings();
                }
            });

            // Trigger action hook for other scripts
            window.dispatchEvent(
                new CustomEvent("accessibility:init", {
                    detail: { settings: this.settings },
                }),
            );
        },

        /**
         * Load settings and profiles from localized data
         */
        async loadSettings() {
            this.loading = true;
            try {
                // Get data from wp_localize_script instead of REST API
                if (typeof window.accessibilityData !== "undefined") {
                    this.profiles = window.accessibilityData.profiles || {};
                    this.settingsDefinition = window.accessibilityData.settingsDefinition || {};
                    this.nonce = window.accessibilityData.nonce || null;
                    this.restUrl = window.accessibilityData.restUrl || null;
                    this.displayMode = window.accessibilityData.displayMode || 'both';

                    // Set initial view based on display mode
                    if (this.displayMode === 'buttons') {
                        this.currentView = 'buttons';
                    } else {
                        this.currentView = 'detailed';
                    }
                } else {
                    console.warn("Accessibility - window.accessibilityData is undefined");
                }
            } catch (error) {
                console.error("Failed to load accessibility settings:", error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Restore settings from localStorage (for persistence across page loads)
         */
        restoreFromLocalStorage() {
            try {
                const stored = localStorage.getItem("a11y_settings");
                if (stored) {
                    const localSettings = JSON.parse(stored);

                    // Validate and sanitize old localStorage data
                    const sanitizedSettings = this.sanitizeSettings(localSettings);

                    this.settings = { ...this.settings, ...sanitizedSettings };
                    // Don't call applySettings() here - preload code already applied settings
                    // applySettings() is only called when user changes settings via updateField()
                }

                // Also restore selected profile
                const storedProfile = localStorage.getItem("a11y_selected_profile");
                if (storedProfile) {
                    this.selectedProfile = storedProfile;
                }

                // Restore view preference (only if displayMode allows switching)
                if (this.displayMode === 'both') {
                    const storedView = localStorage.getItem("a11y_current_view");
                    if (storedView && (storedView === 'detailed' || storedView === 'buttons')) {
                        this.currentView = storedView;
                    }
                }
            } catch (error) {
                console.error("Failed to restore localStorage settings:", error);
            }
        },

        /**
         * Apply settings to the DOM by executing toggle handlers
         */
        applySettings() {
            // Execute toggle handlers for all settings, using default if not currently set
            Object.entries(this.settingsDefinition).forEach(([settingId, setting]) => {
                if (setting.toggle_handler) {
                    // Use current value or default value
                    const value = settingId in this.settings ? this.settings[settingId] : setting.default;
                    this.executeToggleHandler(setting.toggle_handler, value);
                }
            });

            // Trigger custom event for other scripts to listen
            window.dispatchEvent(
                new CustomEvent("accessibility:applied", {
                    detail: { settings: this.settings },
                }),
            );
        },

        /**
         * Update a single setting
         */
        updateField(settingId, value) {
            // Handle checkbox value conversion (string to boolean)
            const settingDef = this.settingsDefinition[settingId];
            if (settingDef && settingDef.type === "boolean") {
                value = value === true || value === "on";
            }

            // Round range values to avoid floating point precision issues
            if (settingDef && settingDef.type === "range") {
                value = parseFloat(parseFloat(value).toFixed(10));
            }

            // Update local state
            this.settings[settingId] = value;

            // Save to localStorage immediately
            this.saveToLocalStorage();

            // Execute only the toggle handler for this specific setting
            if (settingDef && settingDef.toggle_handler) {
                this.executeToggleHandler(settingDef.toggle_handler, value);
            }

            // Track analytics
            this.trackEvent("setting_applied", {
                setting_id: settingId,
                value: value,
            });

            // Trigger custom event
            window.dispatchEvent(
                new CustomEvent("accessibility:fieldChanged", {
                    detail: { settingId, value },
                }),
            );
        },

        /**
         * Execute toggle handler for a setting
         */
        executeToggleHandler(handlerCode, value) {
            try {
                // Create and execute function with value parameter
                const handler = new Function("value", handlerCode);
                handler(value);
            } catch (error) {
                console.error("Error executing toggle handler:", error);
            }
        },

        /**
         * Apply a profile (load preset settings)
         * Selecting empty option resets to defaults
         */
        applyProfile(profileId) {
            if (!profileId) {
                // Empty value selected - reset to defaults
                this.resetToDefault(true);
                return;
            }

            const profile = this.profiles[profileId];
            if (!profile || !profile.settings) return;

            // Start with default values for all settings
            const defaultSettings = {};
            Object.entries(this.settingsDefinition).forEach(([id, setting]) => {
                defaultSettings[id] = setting.default;
            });

            // Sanitize profile settings (handles data migration)
            const sanitizedProfileSettings = this.sanitizeSettings(profile.settings);

            // Apply sanitized profile settings on top of defaults
            // This ensures: settings in profile = profile value, settings not in profile = default
            this.settings = { ...defaultSettings, ...sanitizedProfileSettings };
            this.selectedProfile = profileId;

            // Save to localStorage (both settings and selected profile)
            this.saveToLocalStorage();
            localStorage.setItem("a11y_selected_profile", profileId);

            // Apply changes (toggle handlers executed by applySettings)
            this.applySettings();

            // Track analytics
            this.trackEvent("profile_applied", {
                profile_id: profileId,
                profile_name: profile.name || profileId,
            });

            // Trigger action
            window.dispatchEvent(
                new CustomEvent("accessibility:profileApplied", {
                    detail: { profileId, settings: this.settings },
                }),
            );
        },

        /**
         * Reset to default settings
         */
        resetToDefault(skipConfirm = false) {
            if (
                !skipConfirm &&
                !confirm(
                    window.accessibilityData.confirmReset ||
                        "Are you sure you want reset the accessibility settings back to default?",
                )
            ) {
                return;
            }

            // Reset to default values from settings definition
            const defaultSettings = {};
            Object.entries(this.settingsDefinition).forEach(([id, setting]) => {
                defaultSettings[id] = setting.default;
            });

            this.settings = defaultSettings;
            this.selectedProfile = null;

            // Clear localStorage (but preserve view preference)
            localStorage.removeItem("a11y_settings");
            localStorage.removeItem("a11y_selected_profile");

            // Apply changes (will use default values)
            this.applySettings();

            // Track analytics
            this.trackEvent("settings_reset", {
                reset_to: "defaults",
            });

            // Trigger action
            window.dispatchEvent(
                new CustomEvent("accessibility:reset", {
                    detail: { settings: defaultSettings },
                }),
            );
        },

        /**
         * Save settings to localStorage
         */
        saveToLocalStorage() {
            try {
                localStorage.setItem("a11y_settings", JSON.stringify(this.settings));
            } catch (error) {
                console.error("Failed to save to localStorage:", error);
            }
        },

        /**
         * Save current view preference to localStorage
         */
        saveViewPreference() {
            try {
                localStorage.setItem("a11y_current_view", this.currentView);
            } catch (error) {
                console.error("Failed to save view preference:", error);
            }
        },

        /**
         * Get translation string (fallback for missing translations)
         */
        getTranslation(key) {
            const translations = {
                confirmReset: "Are you sure you want to reset accessibility settings?",
            };
            return translations[key] || key;
        },

        /**
         * Toggle between detailed and button views
         */
        toggleView() {
            this.currentView = this.currentView === 'detailed' ? 'buttons' : 'detailed';
            // Save preference to localStorage
            this.saveViewPreference();
        },

        /**
         * Toggle a boolean setting (for button grid)
         */
        toggleBoolean(settingId) {
            const currentValue = this.settings[settingId] || false;
            this.updateField(settingId, !currentValue);
        },

        /**
         * Increment a range setting (for button grid)
         */
        incrementSetting(settingId, amount = 5) {
            const setting = this.settingsDefinition[settingId];
            if (!setting || setting.type !== 'range') return;

            const currentValue = this.settings[settingId] || setting.default;
            const newValue = Math.min(setting.max, currentValue + amount);
            // Round to avoid floating point precision issues
            const roundedValue = parseFloat(newValue.toFixed(10));
            this.updateField(settingId, roundedValue);
        },

        /**
         * Decrement a range setting (for button grid)
         */
        decrementSetting(settingId, amount = 5) {
            const setting = this.settingsDefinition[settingId];
            if (!setting || setting.type !== 'range') return;

            const currentValue = this.settings[settingId] || setting.default;
            const newValue = Math.max(setting.min, currentValue - amount);
            // Round to avoid floating point precision issues
            const roundedValue = parseFloat(newValue.toFixed(10));
            this.updateField(settingId, roundedValue);
        },

        /**
         * Cycle through select options (for button grid)
         */
        cycleSelectSetting(settingId) {
            const setting = this.settingsDefinition[settingId];
            if (!setting || setting.type !== 'select') return;

            const options = Object.keys(setting.options || {});
            if (options.length === 0) return;

            const currentValue = this.settings[settingId] || setting.default;
            const currentIndex = options.indexOf(String(currentValue));
            const nextIndex = (currentIndex + 1) % options.length;
            this.updateField(settingId, options[nextIndex]);
        },

        /**
         * Track analytics event
         * Fires DOM event for external analytics integrations (Google Analytics, etc.)
         */
        trackEvent(eventName, eventData = {}) {
            // Fire DOM event for other scripts to listen
            window.dispatchEvent(
                new CustomEvent("accessibility:analytics", {
                    detail: { eventName, eventData },
                }),
            );
        },

        /**
         * Validate setting value using REST API
         * Optional - for real-time validation feedback
         */
        async validateSetting(settingId, value) {
            if (!this.restUrl || !this.nonce) {
                // Fallback to client-side validation
                return this.clientSideValidate(settingId, value);
            }

            try {
                const response = await fetch(`${this.restUrl}/settings/validate`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-WP-Nonce": this.nonce,
                    },
                    body: JSON.stringify({
                        setting_id: settingId,
                        value: value,
                    }),
                });

                const data = await response.json();
                return {
                    valid: data.valid,
                    sanitized: data.sanitized,
                };
            } catch (error) {
                console.error("Validation API error:", error);
                return this.clientSideValidate(settingId, value);
            }
        },

        /**
         * Client-side validation fallback
         */
        clientSideValidate(settingId, value) {
            const setting = this.settingsDefinition[settingId];
            if (!setting) return { valid: false, sanitized: null };

            if (setting.type === "range") {
                const numValue = parseFloat(value);
                const valid = numValue >= setting.min && numValue <= setting.max;
                return {
                    valid: valid,
                    sanitized: valid ? numValue : setting.default,
                };
            }

            if (setting.type === "select") {
                const validOptions = Object.keys(setting.options || {});
                const valid = validOptions.includes(String(value));
                return {
                    valid: valid,
                    sanitized: valid ? value : setting.default,
                };
            }

            if (setting.type === "boolean") {
                return {
                    valid: true,
                    sanitized: Boolean(value),
                };
            }

            return { valid: true, sanitized: value };
        },

        /**
         * Sanitize all settings in an object
         * Handles data migration when settings change (e.g., max values reduced)
         *
         * @param {Object} settings - Settings object to sanitize
         * @return {Object} Sanitized settings object
         */
        sanitizeSettings(settings) {
            const sanitized = {};

            for (const [settingId, value] of Object.entries(settings)) {
                const setting = this.settingsDefinition[settingId];

                // Skip if setting no longer exists
                if (!setting) {
                    continue;
                }

                // Validate and sanitize
                const validation = this.clientSideValidate(settingId, value);

                if (validation.valid) {
                    sanitized[settingId] = value;
                } else if (validation.sanitized !== null) {
                    // Use sanitized value (e.g., clamped to new min/max)
                    sanitized[settingId] = validation.sanitized;
                }
                // If both invalid and can't sanitize, skip (won't be in result)
            }

            return sanitized;
        },
    }));
});
